import { useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Chip } from '@/components/ui/Chip'
import { cn } from '@/lib/cn'

/**
 * Chip list plus an inline add with typeahead over the client's existing tags.
 * Tags are client-scoped and shared across entity types, so the suggestion
 * pool is deliberately one list rather than one per entity.
 */
export function TagEditor({
  tags,
  suggestions,
  onChange,
  readOnly = false,
  className,
}: {
  tags: string[]
  suggestions: string[]
  onChange: (next: string[]) => void
  readOnly?: boolean
  className?: string
}) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useRef(`tags-${Math.random().toString(36).slice(2, 8)}`).current

  const available = useMemo(
    () => suggestions.filter((s) => !tags.includes(s)),
    [suggestions, tags],
  )

  function commit(raw: string) {
    const tag = raw.trim()
    if (tag && !tags.includes(tag)) onChange([...tags, tag])
    setDraft('')
    setAdding(false)
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {tags.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          onRemove={readOnly ? undefined : () => onChange(tags.filter((t) => t !== tag))}
        />
      ))}

      {readOnly ? null : adding ? (
        <>
          <input
            ref={inputRef}
            autoFocus
            value={draft}
            list={listId}
            aria-label="New tag"
            placeholder="tag"
            // The editor is often inside a clickable table row or card.
            // Each control stops propagation itself rather than the wrapper
            // doing it, which would make the wrapper a fake interactive element.
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit(draft)
              }
              if (e.key === 'Escape') {
                setDraft('')
                setAdding(false)
              }
            }}
            onBlur={() => commit(draft)}
            className={cn(
              'h-[22px] w-24 rounded-[4px] border border-brand-blue bg-surface px-1.5',
              'text-[11px] outline-none',
            )}
          />
          <datalist id={listId}>
            {available.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setAdding(true)
          }}
          className={cn(
            'inline-flex items-center gap-0.5 rounded-[4px] border border-dashed border-rule-strong',
            'px-1.5 py-0.5 text-[11px] leading-4 text-ink-faint hover:border-brand-blue hover:text-brand-blue',
          )}
        >
          <Plus size={10} aria-hidden />
          tag
        </button>
      )}
    </div>
  )
}
